import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatusCode
} from 'amazon-chime-sdk-js';
import Constants from "./Constants";

export default class AudioVideoClient {
  constructor(params) {
    this.lastHeartbeat = new Map();
    this.heartbeatTimer = null;
    this.monitoringTimer = null;
    // Map attendeeId (or externalUserId) to video tileId
    this.videoMonitoringTileMap = new Map();
    // Map tileId to previous tile state for suspension detection
    this.prevVideoTileState = new Map();

    this.params = params;
    this.params.meetingId = params.meetingId ?? null;
    this.handlers = new Map();
    this.init();
  }

  async init(){
      this.attendees = new Set();
      this.logger = new ConsoleLogger('MyLogger', LogLevel.ERROR);
      this.deviceController = new DefaultDeviceController(this.logger);
      this.lambdaRequestPath = process.env.CHIME_LAMBDA_URL;
      this.isMeetingHost = Boolean(!this.params.meetingId);
      // LOW is the default quality for video monitoring calls.
      this.selectedQuality = Constants.VideoQualityOptions[this.params?.videoQuality] || Constants.VideoQualityOptions.HIGH;
  }

  async startCall() {
    try {
        await this.setInfoFromServer();
        return this.meetingId;
    } catch (e){
      if (this.params.meetingId){
        console.error("Error joining meeting: " + e);
      } else {
        console.error("Error starting call: " + e);
      }
        return null;
    }
  }

  async setInfoFromServer(){
      let that = this;
      let options = {
        method:'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body:JSON.stringify({
          action: "DO_MEETING",
          clientId: `${this.params.clientId}`,
          meetingId: `${this.params.meetingId}`})
      }

      return new Promise((resolve, reject) => {
        fetch(this.lambdaRequestPath, options)
        .then(function(response) {
            return response.json();
        })
        .then(function(data) {
            if (data.statusCode === 200 && data.body){
              that.setMeetingAndAttendeeInfo(JSON.parse(data.body)).then((meetingData) => {
                resolve(meetingData);
              });
            } else {
              reject(data.body);
            }
        })
        .catch(function(arg) {
            console.log('Error: ' + arg);
            reject();
        });
      })
  }

  async setMeetingAndAttendeeInfo(response){
      try{
        this.meetingResponse = response.Meeting;
        this.attendeeResponse = response.Attendee;
        this.meetingId = this.meetingResponse.Meeting.MeetingId;
        this.attendeeId = this.attendeeResponse.Attendee.AttendeeId;
        this.createMeetingSession();
      }catch(err){
          console.log(err)
      }
  }

  createMeetingSession(){
      const configuration = new MeetingSessionConfiguration(this.meetingResponse, this.attendeeResponse);
      this.meetingSession = new DefaultMeetingSession(configuration, this.logger, this.deviceController);
      this.configurationsBeforeSession();
  }

  configurationsBeforeSession(){
      this.configureDevicesForSession();
  }

  async configureDevicesForSession(){
      const videoInputDevices = await this.meetingSession.audioVideo.listVideoInputDevices();
      const audioInputDevices = await this.meetingSession.audioVideo.listAudioInputDevices();
      const audioOutputDevices = await this.meetingSession.audioVideo.listAudioOutputDevices();

      await this.setCamera(videoInputDevices);
      await this.setMicrophone(audioInputDevices);
      await this.setSpeakers(audioOutputDevices);
      this.startSession();

      this.subscribeToFatalErrors();
  }

  /**
   * Starts sending periodic heartbeat messages to notify other attendees of this client's presence.
   * The heartbeat contains the client ID and timestamp, sent every HEARTBEAT_CHECK_INTERVAL_MS milliseconds.
   * If a heartbeat timer already exists, it is cleared before starting a new one.
   * Heartbeat messages are sent using the meetingSession's realtimeSendDataMessage with a ms lifetime.
   */
  startHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = setInterval(() => {
      try {
        this.meetingSession.audioVideo.realtimeSendDataMessage(
            Constants.EventTypes.HEARTBEAT,
            JSON.stringify({ attendeeId: this.params.clientId, ts: Date.now() }),
            1000
        );
        //console.log(`Sent heartbeat to ${this.attendees.size} attendees`);
      } catch (err) {
        console.error("Failed to send heartbeat", err);
      }
    }, Constants.VideoCapture.HEARTBEAT_CHECK_INTERVAL_MS);
  }

  /**
   * Subscribes to fatal error events from the meeting session's audioVideo component.
   */
  subscribeToFatalErrors(){
    this.meetingSession.audioVideo.realtimeSubscribeToFatalError((error) => {
      console.error("Fatal error received: ", error);
      this.params.endCallWithErrorCallback?.(this.params.testerIdentifier);
    });
  }

  async setMicrophone(audioInputDevices) {
    // Skip microphone sharing for video monitoring calls
    if (this.params.isVideoMonitoringCall) return;

    let defaultGroupId = audioInputDevices[0].groupId;
    let filteredAudioInputDevices = AudioVideoClient.removeDefaultDevice(audioInputDevices);
    let micId = this.params.micId ?? AudioVideoClient.getDefaultDeviceId(defaultGroupId, filteredAudioInputDevices) ?? filteredAudioInputDevices[0].deviceId;
    if (!AudioVideoClient.isDeviceAvailable(micId, filteredAudioInputDevices)) {
      this.params.errorCallback?.('microphone', new Error('Microphone not available'));
      return;
    }
    this.activeMicId = micId;

    //For some reason, without this timeout it will incorrectly set the audio device to MediaStreamAudioDestinationNode.
    await new Promise(resolve => setTimeout(resolve, 1500));
    await this.meetingSession?.audioVideo?.startAudioInput(micId);
  }

  async setSpeakers(audioOutputDevices) {
    let audioElement = document.getElementById('rtp_audio_element');
    if (!audioElement){
      $('body').append(`<audio id="rtp_audio_element" class="rtpIcon-no-display"></audio>`);
      audioElement = document.getElementById('rtp_audio_element');
    }
    await this.meetingSession?.audioVideo?.bindAudioElement(audioElement);
    // If safari, iOS or firefox do not need to set audio output.
    if (RTP.Util.isIpad() || RTP.Util.isSafari() || RTP.Util.isFirefox()){
      return;
    }
    let defaultGroupId = audioOutputDevices[0].groupId;
    let filteredAudioOutputDevices = AudioVideoClient.removeDefaultDevice(audioOutputDevices);
    let speakerId = this.params.speakerId ?? AudioVideoClient.getDefaultDeviceId(defaultGroupId, filteredAudioOutputDevices) ?? filteredAudioOutputDevices[0].deviceId;
    if (!AudioVideoClient.isDeviceAvailable(speakerId, filteredAudioOutputDevices)) {
      this.params.errorCallback?.('speaker', new Error("Speakers not available"));
      return;
    }
    this.activeSpeakerId = speakerId;
    await this.meetingSession?.audioVideo?.chooseAudioOutput(speakerId);
  }

  async setCamera(videoInputDevices) {
    // Skip camera sharing for proctors in video monitoring calls
    if (this.params.isAttendeeProctor && this.params.isVideoMonitoringCall) return;

    if (!this.params.isGroupCall) {
      let filteredVideoInputDevices = AudioVideoClient.removeDefaultDevice(videoInputDevices);
      let cameraId = this.params.cameraId ?? filteredVideoInputDevices[0].deviceId;
      if (!AudioVideoClient.isDeviceAvailable(cameraId, filteredVideoInputDevices)) {
        this.params.errorCallback?.('camera', new Error("Camera not available"));
        return;
      }
      this.activeCameraId = cameraId;

      // Configure video input quality and bandwidth for video monitoring calls
      if (this.params.isVideoMonitoringCall) {
        const { width, height, frameRate, maxBandwidth } = this.selectedQuality;
        this.meetingSession?.audioVideo?.chooseVideoInputQuality(width, height, frameRate);
        this.meetingSession?.audioVideo?.setVideoMaxBandwidthKbps(maxBandwidth);
      }

      // Start video input
      await this.meetingSession.audioVideo.startVideoInput(cameraId);
    }
  }

  startSession(){
    try{
        this.addObservers();
        this.meetingSession.audioVideo.start();
        if (this.params.isVideoMonitoringCall && this.params.isAttendeeProctor) {
          this.startDomMutationObserver();
        }
        if (this.params.isGroupCall) {
            !this.isMeetingHost && this.muteLocalAudio();
            if (this.isMeetingHost){
                $(document).trigger('groupAudioCallInitiated', {meetingId:this.meetingId});
            }
        }
    }catch(err){
      console.log(err);
    }
  }

  /**
   * Watch for DOM mutations that replace video elements.
   * When a UI framework (React, Vue, or any re-render) destroys and recreates
   * a DOM element with the same ID, Chime SDK is still bound to the old
   * (now-detached) element. This observer detects when that happens and
   * re-binds active video tiles to the new elements.
   */
  startDomMutationObserver() {
    if (this.domObserver) return;

    // Track which element objects are currently bound per tester ID
    this.boundElements = new Map();
    this.rebindDebounceTimer = null;

    this.domObserver = new MutationObserver(() => {
      // Debounce: DOM mutations often come in bursts during re-renders
      if (this.rebindDebounceTimer) clearTimeout(this.rebindDebounceTimer);
      this.rebindDebounceTimer = setTimeout(() => {
        this.rebindVideoElements();
        this.rebindDebounceTimer = null;
      }, 100);
    });

    // Observe the entire document body for child additions/removals at any depth
    this.domObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Re-query video elements by ID and re-bind any that have been replaced.
   * Chime's bindVideoElement() is idempotent for the same element, so calling
   * it on an unchanged element is a no-op.
   */
  rebindVideoElements() {
    if (!this.meetingSession?.audioVideo || !this.videoMonitoringTileMap.size) return;

    this.videoMonitoringTileMap.forEach((tileId, externalUserId) => {
      const elementId = this.params.videoPrefix + externalUserId;
      const currentElement = document.querySelector(`#${elementId}`);

      if (!currentElement) return;

      const previousVideoElement = this.boundElements?.get(externalUserId);
      const imgId = this.params?.context?.config?.streamingConfigs?.imagePrefix + externalUserId;
      if (previousVideoElement && previousVideoElement !== currentElement) {
        try {
          this.meetingSession?.audioVideo?.bindVideoElement(tileId, currentElement);
          externalUserId && this.params?.context?.toggleVideoMonitoring(externalUserId, true);
          console.log(`AudioVideoClient: DOM element #${elementId} was replaced — rebinding video tile ${tileId}`);
        } catch (err) {
          console.error(`AudioVideoClient: Failed to rebind video tile ${tileId} to new element #${elementId}:`, err);
        }
      }

      this.boundElements?.set(externalUserId, currentElement);
    });
  }

  addObservers(){
    this.addDeviceChangeObserver();
    this.addSessionLifeCycleObserver();
    this.addAttendeeObserver();
  }

  addDeviceChangeObserver(){
    const deviceChangeObserver = {
        audioInputsChanged: freshAudioInputDeviceList => {
          if (this.activeMicId && AudioVideoClient.isDeviceAvailable(this.activeMicId, freshAudioInputDeviceList)) {
            return;
          }
          this.meetingSession?.audioVideo?.stopAudioInput().then(() => {
              //Since the microphone is changing, mute the audio
              this.muteLocalAudio();
              this.params.localMuteAudioCallback?.();
              return this.setMicrophone(freshAudioInputDeviceList);
          })
          .catch((err) => {
            console.error(`Microphone Error: ${err}`);
            this.params.errorCallback?.('microphone', err);
          });
        },

        audioOutputsChanged: freshAudioOutputDeviceList => {
          if (this.activeSpeakerId && AudioVideoClient.isDeviceAvailable(this.activeSpeakerId, freshAudioOutputDeviceList)) {
            return;
          }
          this.meetingSession?.audioVideo?.unbindAudioElement();
          this.setSpeakers(freshAudioOutputDeviceList).then()
          .catch((err) => {
            console.error(`Speaker Error: ${err}`);
            this.params.errorCallback?.('speaker', err);
          });
        },

        videoInputsChanged: freshVideoInputDeviceList => {
          if (this.activeCameraId && AudioVideoClient.isDeviceAvailable(this.activeCameraId, freshVideoInputDeviceList)) {
            return;
          }
          let muted = this.isLocalVideoMuted();
          this.meetingSession?.audioVideo?.stopVideoInput().then(() => {
            this.muteLocalVideo();
            return this.setCamera(freshVideoInputDeviceList);
          }).then(() => {
            if (!muted) {
              this.unMuteLocalVideo();
            }
          }).catch((err) => {
            console.error(`Camera Error: ${err}`);
            this.params.errorCallback?.('camera', err);
          });
        },

        audioInputMuteStateChanged: (device, muted) => {
          console.log('Device', device, muted ? 'is muted in hardware' : 'is not muted');
        },
      };

      this.meetingSession.audioVideo.addDeviceChangeObserver(deviceChangeObserver);
      this.handlers.set(deviceChangeObserver, this.meetingSession.audioVideo.removeDeviceChangeObserver.bind(this.meetingSession.audioVideo, deviceChangeObserver));
  }

  addSessionLifeCycleObserver(){
    try{
      let localTileId = null;
      const sessionLifeCycleObserver = {

      audioVideoDidStart: () => {
        console.log('Started');
        if (!this.params.isGroupCall) {
          this.meetingSession.audioVideo.startLocalVideoTile();
        }
      },

      audioVideoDidStop: (sessionStatus) => {
        const code = sessionStatus.statusCode();
        const terminal = typeof sessionStatus.isTerminal === 'function' ? sessionStatus.isTerminal() : false;
        const failure = typeof sessionStatus.isFailure === 'function' ? sessionStatus.isFailure() : true; // default to failure when unknown

        console.warn(this.params.isAttendeeProctor ? 'Proctor side: ' : 'Tester side: ');
        console.warn('Video was stopped with status code:', code, '.Is it a terminal:', terminal, '.Is it a failure:', failure);

        if (code === MeetingSessionStatusCode.MeetingEnded || terminal === true) {
          //don't end the meeting if it's a video monitoring call.
          if (this.params.isVideoMonitoringCall) {
            if (!this.params.isAttendeeProctor) {
              console.warn('Video monitoring call ended, so send signal to the proctor for starting a new video monitoring!');
              this.params?.context?.restartVideoMonitoringByTester();
            }else {
                console.warn('Video monitoring call ended for proctor.');
            }
          } else {
            this.endCall().then(
                () => console.warn('A non-videoMonitoring call was ended')
            );

          }
        }
      },

      audioVideoDidStartConnecting: reconnecting => {
        // I see reconnecting true while the user is disconnected from WiFi, so not a reliable listener for reconnecting.
        if (reconnecting) {
          //console.log('Attempting to reconnect');
        }
      },

      /* Bind video tiles to video elements as they are added/removed */
      videoTileDidUpdate: (tileState) => {
        // Ignore tiles not bound to an attendee or in group call mode
        if (!tileState.boundAttendeeId || this.params.isGroupCall) return;

        // Map attendee to attendee external id for video monitoring
        if (this.params.isVideoMonitoringCall && this.params.isAttendeeProctor && tileState.boundExternalUserId) {
          this.videoMonitoringTileMap.set(tileState.boundExternalUserId, tileState.tileId);
          const remoteVideoElementId = this.params.videoPrefix + tileState.boundExternalUserId;
          const remoteVideoElement = document.querySelector(`#${remoteVideoElementId}`);
          if (remoteVideoElement) {
            this.boundElements?.set(tileState.boundExternalUserId, remoteVideoElement);
          }

        }

        // Get previous state
        const prevState = this.prevVideoTileState.get(tileState.boundExternalUserId) || {};

        // Save current state for next update
        this.prevVideoTileState.set(tileState.boundExternalUserId, {
          active: Boolean(tileState.boundVideoStream)
        });

        // Detect mid-call suspension
        if (prevState.active && !tileState.boundVideoStream) {
          //console.warn(`Video for attendee ${tileState.boundExternalUserId || tileState.boundAttendeeId} was suspended during the call.`);
          this.params?.videoIsSuspendedCallback?.();
        }

        //Detect mid-call resumption
        if (!prevState.active && Boolean(tileState?.boundVideoStream)) {
            //console.log(`Video for attendee ${tileState?.boundExternalUserId} has resumed.`);
            this.params?.attendeeConnectedAgainCallback?.(tileState?.boundExternalUserId);
        }

        // Local video
        if (tileState.localTile) {
          if (!this.params.isVideoMonitoringCall) {
            this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.params.localVideoElement);
          }
          localTileId = tileState.tileId;
          if (!tileState.active && !tileState.boundVideoStream) localTileId = null;
          return;
        }

        // Remote video
        if (!tileState.isContent) {
          if (this.params.isVideoMonitoringCall && this.params.isAttendeeProctor) {
            const testerVideoElementId = this.params.videoPrefix + tileState.boundExternalUserId;
            const remoteVideoElement = document.querySelector(`#${testerVideoElementId}`);
            if (remoteVideoElement) {
              this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, remoteVideoElement);
            } else {
              //console.warn(`Video element with id ${testerVideoElementId} not found!`);
            }
          } else {
            this.params?.remoteVideoElement && this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.params.remoteVideoElement);
          }
        }
      },

      videoTileWasRemoved: (tileId) => {
        const externalUserId = [...this.videoMonitoringTileMap.entries()].find(([_, v]) => v === tileId)?.[0];
        if (externalUserId) {
          this.videoMonitoringTileMap.delete(externalUserId);
          this.params.attendeeDisconnectedCallback?.(externalUserId);
          this.prevVideoTileState.set(externalUserId, { active: false });
        }
        if (localTileId === tileId) {
          console.log(`You called removeLocalVideoTile. videoElement can be bound to another tile.`);
          localTileId = null;
        }
      },

      connectionHealthDidChange: (connectionHealthData) => {
        //TODO: implement this if we need to.
        //console.log('Connection health changed:', connectionHealthData);
      },

      connectionDidBecomePoor: () => {
        //console.info('Poor audio quality detected');
      },

      connectionDidSuggestStopVideo: () => {
        //console.info('Poor video quality detected');
      },

      connectionDidBecomeGood: () => {
        //TODO: implement this if we need to.
      }
    }

    this.meetingSession.audioVideo.addObserver(sessionLifeCycleObserver);
    this.handlers.set(sessionLifeCycleObserver, this.meetingSession.audioVideo.removeObserver.bind(this.meetingSession.audioVideo, sessionLifeCycleObserver));
    } catch (err){
      console.log("Error in addSessionLifeCycleObserver: ");
      console.error(err);
    }
  }

  addAttendeeObserver() {
    const callback = (attendeeId, present, externalUserId) => {
      let isAttendeeProctor = externalUserId.startsWith('PROCTOR');
      if (present) {
        this.attendees.add(attendeeId);
        //Proctors subscribe to testers and testers subscribe to proctors, but not testers to testers or proctors to proctors
        if (attendeeId !== this.attendeeId && this.isMeetingHost !== isAttendeeProctor) {
          this.subscribeToVolumeChanges(attendeeId);
          this.params.callAnsweredCallback?.();
        }
        (this.isMeetingHost && attendeeId !== this.attendeeId) && this.handleGroupCallAttendeePresent(externalUserId);
      } else if (this.isMeetingHost) {
        $(document).trigger('studentDroppedGroupCall', externalUserId);
        this.params.attendeeDisconnectedCallback?.(externalUserId);
      }
    };

    this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(callback);
    this.handlers.set(callback, this.meetingSession.audioVideo.realtimeUnsubscribeToAttendeeIdPresence.bind(this.meetingSession.audioVideo, callback));
  };

  subscribeToVolumeChanges(attendeeIdToSubscribeTo) {
    this.meetingSession?.audioVideo?.realtimeSubscribeToVolumeIndicator(attendeeIdToSubscribeTo, this.detectVolumeChange.bind(this));
    this.handlers.set(attendeeIdToSubscribeTo, this.meetingSession?.audioVideo?.realtimeUnsubscribeFromVolumeIndicator.bind(this.meetingSession.audioVideo, attendeeIdToSubscribeTo, this.detectVolumeChange.bind(this)));
  }

  detectVolumeChange(attendeeId, volume, muted, signalStrength, externalUserId) {
    if (muted === null) {
      return;
    }
    this.params.remoteMuteAudioCallback?.(muted, externalUserId);
  }

  handleGroupCallAttendeePresent(externalUserId){
    if (!this.params.isGroupCall) {
      return;
    }
    this.informGroupCallAttendeePresent(externalUserId);
  }

  informGroupCallAttendeePresent(externalUserId){
    $(document).trigger('attendeeJoinedGroupCall',{studentId:externalUserId});
    console.log("Attendee present is: " + externalUserId);
  }

  handleNoAttendeePresent(){
    this.endMeeting();
  }

  async deleteAttendee() {
    try {
      var options = {
        method:'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body:JSON.stringify({
          action: "DELETE_ATTENDEE",
          meetingId: this.meetingId,
          attendeeId: this.attendeeId})
      }
      await fetch(this.lambdaRequestPath, options)
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
          if (data.statusCode == 200){
            console.log("NOTE: END MEETING RESPONSE " + JSON.stringify(data));
          }
      })
      .catch(function(arg) {
          console.log('error: ' + arg);
      });
      this.cleanup();
    }
    catch (err) {
      console.error("Error: " + err);
    }
  }

  async endMeeting() {
    try {
      var options = {
        method:'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body:JSON.stringify({
          action: "END_MEETING",
          meetingId: this.meetingId
        })
      }

      await fetch(this.lambdaRequestPath, options)
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
          if (data.statusCode == 200){
            console.log("NOTE: END MEETING RESPONSE " + JSON.stringify(data));
          }
      })
      .catch(function(arg) {
          console.log('error: ' + arg);
      });
      this.cleanup();
    }
    catch (err) {
      console.error("NOTE Error: " + err);
    }
  }
  //TODO: figure out the warnings..
  //https://github.com/aws/amazon-chime-sdk-js/issues/2796
  async endCall() {
    await this.meetingSession?.audioVideo?.stopVideoInput();
    await this.meetingSession?.audioVideo?.stopAudioInput();
    this.meetingSession?.audioVideo?.stop();
    await this.endMeeting();
  }

  // End video monitoring call for proctor only
  async endVideoMonitoringCall() {
    if (this.params.isAttendeeProctor) {
      await this.meetingSession?.audioVideo?.stopVideoInput();
      await this.meetingSession?.audioVideo?.stopAudioInput();
      this.meetingSession?.audioVideo?.stop();
      await this.endMeeting();
    }
  }

  isLocalAudioMuted() {
    return this.meetingSession?.audioVideo?.realtimeIsLocalAudioMuted();
  }

  muteLocalAudio() {
    this.meetingSession?.audioVideo?.realtimeMuteLocalAudio();
  }

  unMuteLocalAudio() {
    this.meetingSession?.audioVideo?.realtimeUnmuteLocalAudio();
  }

  isLocalVideoMuted() {
    return this.isVideoMuted;
  }

  muteLocalVideo() {
    this.meetingSession?.audioVideo?.stopLocalVideoTile();
    this.isVideoMuted = true;
  }

  unMuteLocalVideo() {
    this.meetingSession?.audioVideo?.startLocalVideoTile();
    this.isVideoMuted = false;
  }

  /**
   * Removes the current attendee (this.attendeeId) from the session when they leave.
   */
  async removeAttendeeByTester() {
    try {
      // Stop publishing local video before tearing down the session
      await this.meetingSession?.audioVideo?.stopLocalVideoTile();
      await this.meetingSession?.audioVideo?.stopVideoInput();
      this.isVideoMuted = true;

      // Unbind any local video if needed
      try { this.meetingSession?.audioVideo?.removeLocalVideoTile(); } catch {
        console.error('Error removing local video tile:', err);
      }

      // Remove attendee state and any bound remote tiles
      this.removeAttendee(this.attendeeId);

      //Delete attendee on server (use when user leaves)
      await this.deleteAttendee(); // calls server DELETE_ATTENDEE and cleanup()

    } catch (err) {
      console.error(`Error stopping local video for attendee ${this.attendeeId}: ${err}`);
    }
  }

  removeAttendeeByProctor(externalUserId) {
    const attendeeId = Array.from(this.attendees).find(attId => {
      const attendeeInfo = this.lastHeartbeat.get(attId);
      return attendeeInfo?.externalUserId === externalUserId;
    });
    if (attendeeId) {
        this.removeAttendee(attendeeId);
    }
  }

  removeAttendee(attendeeId) {
          this.attendees.delete(attendeeId);
          this.lastHeartbeat.delete(attendeeId);
          const tileId = this.videoMonitoringTileMap.get(attendeeId);
          if (tileId !== undefined) {
            this.meetingSession?.audioVideo?.removeVideoTile(tileId);
            this.videoMonitoringTileMap.delete(attendeeId);
          }
  }

  //Remove "default" and "communication" options.  This is important because if you are using your default device, then unplug it, it can switch to a new "default" device,
  // which makes it seem like the device hasn't changed.
  static removeDefaultDevice(devices) {
    if (!devices) {
      return;
    }
    let devicesToRemove = ["default","communications"];
    return devices.filter(device => !devicesToRemove.includes(device.deviceId));
  }

  static isDeviceAvailable(deviceId, devices) {
    return Boolean(devices.find(device => device.deviceId === deviceId));
  }

  //Default devices are in the device list multiple times (in chrome).  We have to remove the default device from the list, but we still want to prioritize the using that device
  //if it's available. The group id of the default device matches the default device that isn't listed as "default".
  static getDefaultDeviceId(groupId, devices) {
    let device = devices.find(device => device.groupId === groupId);
    if (!device) {
      return;
    }
    return device.deviceId;
  }

  clearEventHandlers() {
    if (!this.meetingSession) {
      return;
    }
    for (let handler of this.handlers.keys()) {
      let handlerFunction = this.handlers.get(handler);
      handlerFunction(handler);
      handlerFunction();
    }
  }

  // Reset
  cleanup(){
    this.clearEventHandlers();
    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }
    if (this.rebindDebounceTimer) {
      clearTimeout(this.rebindDebounceTimer);
      this.rebindDebounceTimer = null;
    }
    this.boundElements = null;

    if (this.meetingSession){
      this.meetingSession.audioVideo.realtimeUnsubscribeToFatalError();
      this.meetingSession.audioVideo.realtimeUnsubscribeFromReceiveDataMessage(Constants.EventTypes.HEARTBEAT);
      this.meetingSession.deviceController.destroy();
      this.meetingSession = null;
      //if meeting host - don't preserve the meeting id.
      if (this.isMeetingHost)
      {
        this.meetingId = null;
      }
      this.attendees.clear();
    }

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.monitoringTimer) {
      clearInterval(this.monitoringTimer);
      this.monitoringTimer = null;
    }
  }
}
